import {
  ArchiveRestore,
  BookOpen,
  ChartNoAxesColumn,
  CircleHelp,
  Columns3,
  Eraser,
  FileSearch,
  FileText,
  Home,
  ListTree,
  Menu,
  Moon,
  Scale,
  Settings,
  Sun,
  X,
} from 'lucide-react';
import { useState, type PropsWithChildren } from 'react';
import type { ThemePreference } from '../app/useTheme';
import { BrandMark } from './BrandMark';

export type AppRoute = 'home' | 'rescue' | 'inspect' | 'extract' | 'clean' | 'compare' | 'batch' | 'reports' | 'help' | 'settings' | 'privacy';

interface AppShellProps extends PropsWithChildren {
  route: AppRoute;
  navigate: (route: AppRoute) => void;
  hasReport: boolean;
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
  online: boolean;
  context?: React.ReactNode;
}

const primaryNavigation = [
  { route: 'home', label: 'Home', icon: Home },
  { route: 'rescue', label: 'Rescue', icon: ArchiveRestore },
  { route: 'inspect', label: 'Inspect', icon: FileSearch, reportRequired: true },
  { route: 'extract', label: 'Extract', icon: FileText, reportRequired: true },
  { route: 'clean', label: 'Clean', icon: Eraser, comingSoon: true },
  { route: 'compare', label: 'Compare', icon: Scale, comingSoon: true },
  { route: 'batch', label: 'Batch', icon: ListTree, comingSoon: true },
  { route: 'reports', label: 'Reports', icon: ChartNoAxesColumn, reportRequired: true },
] as const;

export function AppShell({ route, navigate, hasReport, theme, setTheme, online, context, children }: AppShellProps): React.JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);
  const cycleTheme = (): void => setTheme(theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system');
  const go = (target: AppRoute): void => {
    navigate(target);
    setMobileOpen(false);
  };
  return (
    <div className={`app-shell${context ? ' app-shell--context' : ''}`}>
      <header className="topbar">
        <button className="icon-button mobile-only" type="button" aria-label="Open navigation" onClick={() => setMobileOpen(true)}><Menu /></button>
        <BrandMark />
        <div className="topbar__status" aria-label={online ? 'Application online; file processing remains local' : 'Application offline; cached processing remains local'}>
          <span className={`status-dot ${online ? '' : 'status-dot--offline'}`} />
          <span>{online ? 'Processed locally' : 'Offline · still local'}</span>
          <span className="topbar__divider" aria-hidden="true" />
          <button className="theme-button" type="button" onClick={cycleTheme} aria-label={`Appearance preference: ${theme}. Change theme`}>
            {theme === 'dark' ? <Moon /> : theme === 'light' ? <Sun /> : <Columns3 />}
            <span>{theme === 'system' ? 'System' : theme === 'light' ? 'Light' : 'Dark'}</span>
          </button>
        </div>
      </header>
      {mobileOpen ? <button className="nav-scrim" aria-label="Close navigation" onClick={() => setMobileOpen(false)} /> : null}
      <aside className={`sidebar ${mobileOpen ? 'sidebar--open' : ''}`} aria-label="Primary navigation">
        <button className="icon-button sidebar__close mobile-only" type="button" aria-label="Close navigation" onClick={() => setMobileOpen(false)}><X /></button>
        <nav>
          <ul>
            {primaryNavigation.map((item) => {
              const Icon = item.icon;
              const comingSoon = 'comingSoon' in item && item.comingSoon;
              const reportLocked = 'reportRequired' in item && item.reportRequired && !hasReport;
              const disabled = comingSoon || reportLocked;
              return (
                <li key={item.route}>
                  <button
                    type="button"
                    className={`nav-item ${route === item.route ? 'nav-item--active' : ''} ${comingSoon ? 'nav-item--coming-soon' : ''}`}
                    onClick={() => go(item.route)}
                    disabled={disabled}
                    title={comingSoon ? `${item.label} is coming soon` : reportLocked ? 'Analyze a file first' : undefined}
                  >
                    <Icon />
                    <span>{item.label}</span>
                    {comingSoon ? <span className="nav-item__status">Soon</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        <nav className="sidebar__footer" aria-label="Secondary navigation">
          <button className={`nav-item ${route === 'help' ? 'nav-item--active' : ''}`} type="button" onClick={() => go('help')}><CircleHelp /><span>Help</span></button>
          <button className={`nav-item ${route === 'privacy' ? 'nav-item--active' : ''}`} type="button" onClick={() => go('privacy')}><BookOpen /><span>Privacy</span></button>
          <button className={`nav-item ${route === 'settings' ? 'nav-item--active' : ''}`} type="button" onClick={() => go('settings')}><Settings /><span>Settings</span></button>
        </nav>
      </aside>
      <main className="workspace" id="main-content">{children}</main>
      {context ? <aside className="context-rail" aria-label="File details">{context}</aside> : null}
      <a className="skip-link" href="#main-content">Skip to content</a>
    </div>
  );
}
